import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { toast } from 'sonner';
import { Save, ExternalLink } from 'lucide-react';

export default function AdminShopContent() {
  const [form, setForm] = useState({ hero_headline: '', hero_sub: '', hero_image_url: '', store_intro: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/shop/store-content');
      setForm(data);
    } catch { toast.error('Failed to load content'); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/shop/store-content', form);
      toast.success('Store content updated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-5" data-testid="admin-shop-content">
      <div className="h-panel-thin p-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="h-eyebrow">Store</div>
          <div className="h-display text-4xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Storefront Content</div>
          <p className="text-sm opacity-85 mt-1" style={{ color: 'var(--heritage-plum)' }}>Edit the headline, sub-copy, and hero image shown on the public /store page. Live instantly.</p>
        </div>
        <a href="/store" target="_blank" rel="noreferrer" className="h-btn-outline text-xs" data-testid="store-preview-link">
          <ExternalLink size={12} className="mr-1.5" /> Preview /store
        </a>
      </div>

      {loading ? <div className="h-eyebrow">Loading...</div> : (
        <div className="grid lg:grid-cols-2 gap-5">
          <Panel className="p-5 space-y-4">
            <div>
              <div className="h-eyebrow mb-1.5">Hero headline</div>
              <input value={form.hero_headline} onChange={(e) => set('hero_headline', e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}
                data-testid="content-headline" />
            </div>
            <div>
              <div className="h-eyebrow mb-1.5">Sub-copy</div>
              <textarea rows={3} value={form.hero_sub} onChange={(e) => set('hero_sub', e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}
                data-testid="content-sub" />
            </div>
            <div>
              <div className="h-eyebrow mb-1.5">Hero image URL</div>
              <input value={form.hero_image_url} onChange={(e) => set('hero_image_url', e.target.value)} placeholder="https://... (leave blank for default)"
                className="w-full rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}
                data-testid="content-image" />
              <div className="text-[11px] opacity-70 mt-1" style={{ color: 'var(--heritage-plum)' }}>Recommended: 1920 × 460px landscape.</div>
            </div>
            <div>
              <div className="h-eyebrow mb-1.5">Intro paragraph (below hero)</div>
              <textarea rows={4} value={form.store_intro} onChange={(e) => set('store_intro', e.target.value)}
                placeholder="Optional. Additional story shown below the hero heading."
                className="w-full rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}
                data-testid="content-intro" />
            </div>
            <div className="flex justify-end">
              <button onClick={save} disabled={saving} className="h-btn text-xs" data-testid="content-save"><Save size={12} className="mr-1" /> {saving ? 'Saving...' : 'Save changes'}</button>
            </div>
          </Panel>

          <Panel className="p-3 overflow-hidden">
            <div className="h-eyebrow px-2 pt-1 pb-2">Preview</div>
            <div className="relative w-full overflow-hidden rounded-xl" style={{ background: '#4B1838', minHeight: 220 }}>
              {form.hero_image_url ? (
                <img src={form.hero_image_url} alt="hero preview" className="w-full max-h-[280px] object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              ) : (
                <div className="h-40 flex items-center justify-center text-xs opacity-70" style={{ color: '#F6F0E4' }}>Default hero will be used.</div>
              )}
            </div>
            <div className="p-5 text-center">
              <div className="h-eyebrow">The Store</div>
              <div className="h-display text-3xl mt-1" style={{ color: 'var(--heritage-plum)' }}>{form.hero_headline || 'Heritage Signature Collection'}</div>
              <p className="text-sm opacity-85 mt-2" style={{ color: 'var(--heritage-plum)' }}>{form.hero_sub}</p>
              {form.store_intro && (
                <p className="text-sm opacity-75 mt-3 italic" style={{ color: 'var(--heritage-plum)' }}>{form.store_intro}</p>
              )}
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}
